import { type FormEvent } from 'react';
import clsx from 'clsx';
import { type Control, type FieldValues, type UseFormSetError } from 'react-hook-form';
import { BorrowerInputVariables, InputLabel } from '@sparrow-card/sparrow-card-constants';
import Button from 'components/Button';
import { ButtonVariant } from 'components/Button/Button';
import Checkbox from 'components/Checkbox';
import DocumentViewer from 'components/DocumentViewer';
import FormContainer from 'components/FormContainer';
import {
  DateOfBirthInput,
  FirstNameInput,
  LastNameInput,
  LocationInput,
  MiddleInitialInput,
  PhoneNumberInput,
  SsnInput,
  TotalMonthlyIncomeInput,
} from 'components/Input/templates';
import { TooltipMessage } from 'enums/Messages';
import { LocationInputsLabels, LocationInputsNames } from 'pages/Apply/common/FormConstants';
import { getPdfPath } from 'pages/Apply/common/getPdfPath';
import Police from 'pages/Apply/common/Police';
import {
  APPLY_TITLE,
  CHECK_BOX_COMMUNICATIONS_LABEL,
  CHECK_BOX_PHONE_CONTACT_LABEL,
  CHECK_BOX_REPORT_LABEL,
  CHECK_BOX_TERM_LABEL,
  CHECK_BOX_TEXT_MESSAGES_LABEL,
  DEMO_TEXT_MESSAGES_CHECKBOX,
  DemoApplyFormRequiredFields,
  LocationInputsRequired,
  Paragraph,
} from './formConstants';
import styles from './Form.module.scss';

interface FormProps {
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  control: Control<FieldValues, object>;
  clearErrors: (name: string) => void;
  setError: UseFormSetError<FieldValues>;
}

const Form = ({ handleSubmit, control, clearErrors, setError }: FormProps) => {
  return (
    <FormContainer onSubmit={handleSubmit} className={styles.container} autoComplete="off">
      <h1 className={styles.title}>{APPLY_TITLE}</h1>
      <h2 className={styles.paragraph}>{Paragraph.Apply}</h2>
      <div className={clsx(styles.form)}>
        <div className={styles.mainFields}>
          <div className={styles.row}>
            <FirstNameInput
              name={BorrowerInputVariables.FirstName}
              label={InputLabel.FirstName}
              containerClassName={styles.firstNameInput}
              required={DemoApplyFormRequiredFields[BorrowerInputVariables.FirstName] ?? true}
            />
            <MiddleInitialInput
              name={BorrowerInputVariables.MiddleInitial}
              label={InputLabel.MiddleInitial}
              containerClassName={styles.middleInitialInput}
              required={DemoApplyFormRequiredFields[BorrowerInputVariables.MiddleInitial] ?? false}
            />
          </div>
          <div className={styles.row}>
            <LastNameInput
              name={BorrowerInputVariables.LastName}
              label={InputLabel.LastName}
              containerClassName={styles.lastNameInput}
              required={DemoApplyFormRequiredFields[BorrowerInputVariables.LastName] ?? true}
            />
            <DateOfBirthInput
              name={BorrowerInputVariables.DateOfBirth}
              label={InputLabel.DateOfBirth}
              containerClassName={styles.dateOfBirthInput}
              required={DemoApplyFormRequiredFields[BorrowerInputVariables.DateOfBirth] ?? true}
            />
          </div>
          <PhoneNumberInput
            name={BorrowerInputVariables.Phone}
            label={InputLabel.Phone}
            containerClassName={styles.phoneInput}
            required={DemoApplyFormRequiredFields[BorrowerInputVariables.Phone] ?? true}
          />
          <div className={styles.locationInputs}>
            <LocationInput
              streetTooltip={TooltipMessage.Street}
              labels={LocationInputsLabels}
              names={LocationInputsNames}
              classNames={{
                street: styles.streetInput,
                unit: styles.unitInput,
                city: styles.cityInput,
                state: styles.stateInput,
                zip: styles.zipInput,
              }}
              requiredFields={LocationInputsRequired}
            />
          </div>
          <div className={styles.row}>
            <SsnInput
              name={BorrowerInputVariables.SSN}
              label={InputLabel.SSN}
              containerClassName={styles.ssnInput}
              required={DemoApplyFormRequiredFields[BorrowerInputVariables.SSN] ?? true}
            />
            <TotalMonthlyIncomeInput
              name={BorrowerInputVariables.TotalMonthlyIncome}
              label={InputLabel.TotalMonthlyIncome}
              containerClassName={styles.incomeInput}
              required={
                DemoApplyFormRequiredFields[BorrowerInputVariables.TotalMonthlyIncome] ?? true
              }
            />
          </div>
        </div>
      </div>
      <h2 className={styles.paragraph}>{Paragraph.Review}</h2>
      <DocumentViewer pdfPath={getPdfPath(null)} className={styles.document} />
      <Checkbox
        name={BorrowerInputVariables.CheckBoxTerms}
        control={control}
        label={CHECK_BOX_TERM_LABEL}
        clearErrors={clearErrors}
        setError={setError}
        required={DemoApplyFormRequiredFields[BorrowerInputVariables.CheckBoxTerms] ?? true}
      />
      <Checkbox
        name={BorrowerInputVariables.CheckBoxElectronicCommunications}
        control={control}
        label={CHECK_BOX_COMMUNICATIONS_LABEL}
        clearErrors={clearErrors}
        setError={setError}
        required={
          DemoApplyFormRequiredFields[BorrowerInputVariables.CheckBoxElectronicCommunications] ??
          true
        }
      />
      <Checkbox
        name={BorrowerInputVariables.CheckBoxContracted}
        control={control}
        label={CHECK_BOX_PHONE_CONTACT_LABEL}
        clearErrors={clearErrors}
        setError={setError}
        required={DemoApplyFormRequiredFields[BorrowerInputVariables.CheckBoxContracted] ?? true}
      />
      <Checkbox
        name={DEMO_TEXT_MESSAGES_CHECKBOX}
        control={control}
        label={CHECK_BOX_TEXT_MESSAGES_LABEL}
        clearErrors={clearErrors}
        setError={setError}
        required
      />
      <Checkbox
        name={BorrowerInputVariables.CheckBoxCreditReport}
        control={control}
        label={CHECK_BOX_REPORT_LABEL}
        clearErrors={clearErrors}
        setError={setError}
        required={DemoApplyFormRequiredFields[BorrowerInputVariables.CheckBoxCreditReport] ?? true}
      />
      <Police />
      <Button type="submit" title="Submit" variant={ButtonVariant.Gradient} />
    </FormContainer>
  );
};

export default Form;
